import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../Components/AddRelationshipTopBar";
import { useHeader } from "../../Providers/HeaderContextProvider";
import DialogueCard from "./sections/DialogueCard";
import LifeAreaTile from "./sections/LifeAreaTile";
import { t, LIFE_AREA_COPY } from "./copy";

/**
 * Shared chrome for the Explore sub-pages (Life Area, Self-Knowledge, Current
 * Focus — Figma 9139:19781 / 9139:19448). Same reasoning as
 * `Pages/Journey/shared.jsx`: the global HeaderComponent floats a transparent
 * bar over the top of the page which swallows clicks aimed at a back button,
 * so these screens hide it (restoring on unmount) and render the app-standard
 * TopBar themselves, with an explicit back target that survives deep links.
 */
export const useStandaloneExploreHeader = () => {
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  useEffect(() => {
    setHeaderContent(false);
    setHeaderContentRightSideContent(false);
    return () => {
      setHeaderContent(true);
      setHeaderContentRightSideContent(true);
    };
  }, [setHeaderContent, setHeaderContentRightSideContent]);
};

export const ExploreBackHeader = ({ to = "/explore", title }) => {
  const navigate = useNavigate();
  return (
    <div className="ex-topbar">
      <TopBar
        title={title}
        showBackButton
        useRegularArrow
        onBackClick={() => navigate(to)}
      />
    </div>
  );
};

/**
 * The content of an Explore sub-page, below its TopBar. On a desktop it is the
 * white rounded card My Connections uses (so the page reads as a desktop page,
 * not a phone layout stretched wide); on a phone it adds nothing.
 */
export const ExploreSubpageCard = ({ children }) => (
  <div className="ex-subcard">{children}</div>
);

/**
 * The area hero (icon + title + description). `area` comes from
 * `resolveLifeArea`. Plan 49: the area's header artwork fills the hero behind a
 * soft shade that keeps the white text readable; without it, the area colour.
 */
export const heroBackground = (area) =>
  area.headerUrl
    ? `linear-gradient(180deg, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0.42) 100%), center / cover no-repeat url("${area.headerUrl}"), ${area.color}`
    : area.color;

export const AreaHero = ({ area, title, description }) => (
  <header className="explore-hero ex-la-hero" style={{ background: heroBackground(area) }}>
    <span className="ex-la-hero__row">
      <LifeAreaTile area={area} className="ex-la-hero__tile" />
      <h1 className="ex-la-hero__title">{title || area.title}</h1>
    </span>
    {(description || area.description) && (
      <p className="ex-la-hero__desc">{description || area.description}</p>
    )}
  </header>
);

/**
 * One stacked-cards block (`RECOMMENDED_FOR_YOU` / `EXPLORE_MORE`): uppercase
 * eyebrow, optional subtitle, light DialogueCards. Renders whatever the
 * section carries — an omitted or empty section is the caller's decision.
 */
export const AreaCardsSection = ({
  section,
  titleFallback,
  subtitleFallback,
  onOpenDialogue,
}) => {
  const cards = section?.cards || [];
  if (cards.length === 0) return null;
  const subtitle = section?.subtitle_key
    ? t(section.subtitle_key, subtitleFallback)
    : subtitleFallback;
  return (
    <section className="ex-section">
      <div className="ex-lrow-head">
        <h2 className="ex-lrow-head__title">{t(section?.title_key, titleFallback)}</h2>
        {subtitle && <p className="ex-lrow-head__sub">{subtitle}</p>}
      </div>
      <div className="ex-lrow-list">
        {cards.map((card, i) => (
          <DialogueCard
            key={card.dialogue_id || i}
            card={card}
            variant="row"
            onOpen={onOpenDialogue}
          />
        ))}
      </div>
    </section>
  );
};

/** The dark "Talk with Aris" footer card (Figma 9139:19781, bottom of column). */
export const TalkWithArisFooter = ({ section, onTalkWithAris }) => (
  <section className="ex-la-talk">
    <h2 className="ex-la-talk__title">
      {t(section?.title_key, "Talk with Aris")}
    </h2>
    <p className="ex-la-talk__body">
      {t(
        section?.prompt_key,
        "Not sure where to begin? Talk with Aris about what's happening, and Aris will help you identify the next best conversation."
      )}
    </p>
    <button type="button" className="ex-la-talk__btn" onClick={onTalkWithAris}>
      {LIFE_AREA_COPY.talkCta}
    </button>
  </section>
);
